import { css } from '@emotion/css';
import { useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';

import { GrafanaTheme2 } from '@grafana/data';
import { Button, Field, Input, Stack, Checkbox, Toggletip, useStyles2 } from '@grafana/ui';
import { notifyApp } from 'app/core/actions';
import { createSuccessNotification } from 'app/core/copy/appNotification';
import { t } from 'app/core/internationalization';
import { ReportBaseV2 } from 'app/extensions/types/reports';
import { dispatch } from 'app/store/store';

import { validateMultipleEmails } from '../../../utils/validators';

interface FormValues {
  previewEmail: string;
  useReportEmails: boolean;
}

export function SendPreviewToggletip() {
  const styles = useStyles2(getStyles);
  const [isOpen, setIsOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { watch: watchMainForm } = useFormContext<ReportBaseV2>();
  const recipients = watchMainForm('recipients') || [];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      previewEmail: '',
      useReportEmails: false,
    },
  });

  const useReportEmails = watch('useReportEmails');

  const onSubmit = (data: FormValues) => {
    setIsSending(true);
    // Simulate sending process
    setTimeout(() => {
      setIsSending(false);
      handleClose();
      dispatch(notifyApp(createSuccessNotification(t('share-report.send-preview.success', 'Email has been sent'))));
    }, 2000);
  };

  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  const content = (
    <div className={styles.container}>
      <Stack direction="column" gap={1.5}>
        <Field
          label={t('share-report.send-preview.email', 'Email')}
          required
          description={t(
            'share-report.send-preview.email-description',
            'Separate multiple email addresses with a comma or semicolon'
          )}
          error={errors.previewEmail?.message}
          invalid={!!errors.previewEmail}
        >
          <Input
            placeholder="your.address@company.com"
            {...register('previewEmail', {
              required: t('share-report.send-preview.email-required', 'Email is required'),
              validate: (value) =>
                validateMultipleEmails(value) ||
                t('share-report.recipients.invalid-emails', 'Invalid emails: {{emails}}', {
                  emails: value,
                }),
            })}
            onKeyDown={handleKeyDown}
            disabled={useReportEmails}
          />
        </Field>
        <Field>
          <Checkbox
            disabled={!recipients.length}
            label={t('share-report.send-preview.use-report-emails', 'Use emails from report')}
            {...register('useReportEmails', {
              onChange: (e) => {
                const checked = e.target.checked;
                setValue('useReportEmails', checked);
                if (checked) {
                  setValue('previewEmail', recipients.join(', '));
                  clearErrors('previewEmail');
                } else {
                  setValue('previewEmail', '');
                }
              },
            })}
          />
        </Field>
        <Stack gap={2} justifyContent="flex-start" direction={{ xs: 'column', sm: 'row' }}>
          <Button variant="secondary" disabled={isSending} onClick={handleClose}>
            {t('share-report.send-preview.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isSending}
            icon={isSending ? 'fa fa-spinner' : undefined}
          >
            {isSending
              ? t('share-report.send-preview.sending', 'Sending...')
              : t('share-report.send-preview.send', 'Send')}
          </Button>
        </Stack>
      </Stack>
    </div>
  );

  return (
    <Toggletip
      content={content}
      title={t('share-report.send-preview.title', 'Send preview')}
      placement="auto-start"
      theme="info"
      show={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={handleClose}
    >
      <Button variant="secondary" fill="outline" onClick={(prevState) => setIsOpen(!prevState)}>
        {t('share-report.send-preview.button', 'Send preview')}
      </Button>
    </Toggletip>
  );
}

const getStyles = (theme: GrafanaTheme2) => {
  return {
    container: css({
      width: '400',
    }),
  };
};
